import { useCallback, useReducer } from 'react';
import { produce } from 'immer';
import { ValueDisplay } from '@zen/common-utils';
import { FpUtils, OracleService } from '@zen/common-app-parts';
import oracles from '../../utils/oracles';
import {
  setter,
  setAsset1,
  setAmount1,
  setOracle,
  setResult,
  setFetchPhase,
} from './modules/setters';

const ACTIONS = {
  SET: 'SET',
  RESET: 'RESET',
  PROGRESS: 'PROGRESS',
};

export default function useRedeemStore({ nodeUrl, initialState = {} }) {
  const [_state, dispatch] = useReducer(reducer, { ...State, ...initialState });
  /** @type {initialState} */
  const state = _state;

  const valid = state.asset1.valid && state.amount1.valid;
  const oracleUrl = state.oracle ? state.oracle.serviceUrl : '';

  const generate = useCallback(async () => {
    if (state.asset1.valid) {
      dispatch({
        type: ACTIONS.SET,
        payload: { prop: 'result', value: { messageBody: '', error: '' } },
      });
      dispatch({ type: ACTIONS.PROGRESS, payload: { value: true } });
      try {
        const {
          start,
          ticker,
          expiry,
          contractId,
          price,
          publicKey,
          asset,
        } = await OracleService.GetMint({
          nodeUrl,
          dispatch,
          assetValue: state.asset1.value,
        });
        if (publicKey !== state.oracle.publicKey || contractId !== state.oracle.contractId)
          throw new Error('Not redeemable by this provider');

        const { queryResult } = await OracleService.GetQuery({
          oracleUrl,
          mintData: {
            expiry,
            start,
            ticker,
          },
          dispatch,
        });
        const [
          {
            index,
            path,
            root,
            timestamp,
            item: { value },
          },
        ] = queryResult;

        let position;
        try {
          position = FpUtils.getPosition({
            assetId: state.asset1.value,
            contractId,
            publicKey,
            start,
            ticker,
            expiry,
            price,
            asset,
          }).position;
        } catch (error) {
          throw new Error('Error parsing the asset in order to get the position');
        }

        const messageBody = FpUtils.getMessageBody({
          isRedeem: true,
          auditPath: path,
          index,
          root,
          timestamp,
          value,
          oracleContractId: contractId,
          oraclePubKey: publicKey,
          position,
          price,
          start,
          ticker,
          expiry,
          asset,
        });

        dispatch({ type: ACTIONS.SET, payload: { prop: 'result', value: { messageBody } } });
        dispatch({ type: ACTIONS.PROGRESS, payload: { value: false } });
      } catch (error) {
        dispatch({
          type: ACTIONS.SET,
          payload: { prop: 'result', value: { messageBody: '', error: error.message } },
        });
        dispatch({ type: ACTIONS.PROGRESS, payload: { value: false } });
        throw error;
      }
    }
  }, [state, nodeUrl, oracleUrl]);

  return {
    state: { ...state, valid },
    actions: {
      setOracle: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'oracle', value } });
      }, []),
      setAsset1: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'asset1', value } });
      }, []),
      setAmount1: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'amount1', value } });
      }, []),
      reset: useCallback(() => {
        dispatch({ type: ACTIONS.RESET });
      }, []),
      generate,
    },
  };
}

const State = {
  oracle: oracles[0],
  asset1: {
    value: '',
    valid: false,
  },
  amount1: {
    value: ValueDisplay.create(),
    valid: false,
  },
  result: {
    error: '',
    messageBody: '',
  },
  progress: false,
  fetchPhase: '', // node-fetch|node-done|oracle-fetch|oracle-done
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case ACTIONS.SET:
      setter({ PropsSettersMap, draft, prop: action.payload.prop, value: action.payload.value });
      break;
    case ACTIONS.PROGRESS:
      draft.progress = action.payload.value;
      break;
    case ACTIONS.RESET:
      reset(draft);
      break;
    default:
      throw new Error(`Unhandled action type ${action.type}`);
  }
});

const PropsSettersMap = {
  oracle: setOracle,
  asset1: setAsset1,
  amount1: setAmount1,
  result: setResult,
  fetchPhase: setFetchPhase,
};

function reset(draft) {
  setAsset1('', draft);
  setAmount1('', draft);
  setResult({ error: '', messageBody: '' }, draft);
}
